import { Suspense, useState, useEffect, useCallback, useMemo, Dispatch } from 'react';
import { useLocation, useNavigate, Outlet, useResolvedPath, useSearchParams } from 'react-router-dom';
import { LoadingDots, ProgressBar, SiteHeader, ActionMenuItem, FlosIcon } from '@flos/react-ui';
import track from 'c9-tracking';
import { useConfigContext } from '../context/ConfigContext';
import { useUserContext } from '../context/UserContext';
import { useFlowContext } from '../context/FlowContext';
import { useStateValue } from '../hooks/UseStateValue';
import { pages } from '../PageConfig';
import { finalizePurchase, productName, login, saveToCache, setError } from '../helpers/shared';
import { PageNavigationWrapper } from '../pages/PageComponentWrapper';
import { PageNavigation } from '../components/PageNavigation';
import { usePackageContext } from '../context/PackageContext';
import ec from '../helpers/ecommerce';
import {
    IAction,
    IPartnerForm,
    IRouterPage,
    ProductNames,
    ProductPage,
    States,
    TError,
    WhitelabelPartner
} from '../interfaces/interfaces';
import { defaultTracking } from '../helpers/tracking';
import { Giftcard } from '../components/Giftcard/Giftcard';
import { NordeaBanner } from '../components/NordeaBanner/NordeaBanner';
import { SET_ACTIVE_PAGE } from '../store/ActionKeys';
import { setFieldValidations, setSelectionValues } from '../store/Actions';
import WhiteLabel from '../components/WhiteLabel';
import { useLoggerContext } from '../context/LoggerContext';
import { useVehicleContext } from '../context/VehicleContext';

/* ========================================= Local Types ========================================= */
type TUseInitiateWhiteLabelPartner = (
    product: ProductNames,
    selection: States['selection'],
    fieldValidation: States['fieldValidation'],
    dispatch: Dispatch<IAction>
) => void;

type TGetWhitelabelPartner = (partnerParams: string | null, isBabysam: boolean) => WhitelabelPartner;

type TGetCustomerDetails = (
    customerDetailsCache: IPartnerForm['customerDetails'],
    isBabysam: boolean
) => { customerDetails: any; fieldValidation: any };

/* ========================================= Local Functions ========================================= */
const useGetProcessedPathname = (pathname: string) => {
    const resolvedPath = useResolvedPath(pathname);
    const processedPathname = useMemo(() => {
        const decodedPathname = decodeURIComponent(resolvedPath.pathname);
        const pathArr = decodedPathname.split('');
        const hasTrailingSlash = pathArr.length > 1 && pathArr[pathArr.length - 1] === '/';
        return hasTrailingSlash ? decodedPathname.slice(0, -1) : decodedPathname;
    }, [resolvedPath.pathname]);

    return processedPathname;
};

const useInitiateWhiteLabelPartner: TUseInitiateWhiteLabelPartner = (product, selection, fieldValidation, dispatch) => {
    const [searchParams] = useSearchParams();

    const [hasInitPartner, setHasInitPartner] = useState<boolean>(false);

    const getWhitelabelPartner: TGetWhitelabelPartner = useCallback((partnerParams, isBabysam) => {
        let whitelabelPartner = WhitelabelPartner.NONE;

        if (isBabysam) {
            whitelabelPartner = WhitelabelPartner.TEDDY_BEAR;
        } else if (!!partnerParams) {
            const lowercasePartnerParams = partnerParams.toLowerCase();

            if (lowercasePartnerParams === 'dbr') {
                whitelabelPartner = WhitelabelPartner.DANSK_BILBRANCHERÅD;
            } else if (lowercasePartnerParams === 'nordea') {
                whitelabelPartner = WhitelabelPartner.NORDEA;
            }
        }

        return whitelabelPartner;
    }, []);

    const getCustomerDetails: TGetCustomerDetails = useCallback((customerDetailsCache, isBabysam) => {
        let customerDetails = {};
        let fieldValidation = {};

        if (isBabysam && !!customerDetailsCache) {
            customerDetails = {
                address: {
                    kvhx: customerDetailsCache.kvhx,
                    address: customerDetailsCache.address,
                    addressInfo: {
                        zipCode: '',
                        streetName: '',
                        houseNumber: '',
                        postalCodeName: '',
                        supCityName: '',
                        floor: '',
                        door: ''
                    },
                    status: 'valid',
                    event: '',
                    kvhxUrl: ''
                },
                fullName: `${customerDetailsCache.firstName} ${customerDetailsCache.lastName}`,
                firstName: customerDetailsCache.firstName,
                lastName: customerDetailsCache.lastName,
                mobilePhone: customerDetailsCache.phoneDetails.phoneNo,
                emailAddress: customerDetailsCache.email
            };

            fieldValidation = {
                ...(customerDetailsCache.firstName && customerDetailsCache.lastName && { fullName: true }),
                ...(customerDetailsCache.firstName && { firstName: true }),
                ...(customerDetailsCache.lastName && { lastName: true }),
                ...(customerDetailsCache.phoneDetails.phoneNo && { mobilePhone: true }),
                ...(customerDetailsCache.email && { emailAddress: true }),
                ...(customerDetailsCache.address && customerDetailsCache.kvhx && { address: true })
            };
        }

        return { customerDetails, fieldValidation };
    }, []);

    useEffect(() => {
        if (!hasInitPartner) {
            setHasInitPartner(true);

            const partnerParams = searchParams.get('partner') ?? searchParams.get('p');
            const partnerCache: IPartnerForm = JSON.parse(sessionStorage.getItem('post-form-babysam')!);
            const customerDetailsCache = partnerCache?.customerDetails;

            const isChild = product === ProductNames.CHILD;
            const isBabysam = isChild && partnerCache?.partnerDetails?.partner?.toLowerCase() === 'babysam';

            const whitelabelPartner = getWhitelabelPartner(partnerParams, isBabysam);
            const { customerDetails, fieldValidation: updatedFieldValidation } = getCustomerDetails(
                customerDetailsCache,
                isBabysam
            );

            setSelectionValues(
                dispatch,
                selection,
                {
                    partner: whitelabelPartner,
                    ...customerDetails
                },
                true
            );

            if (Object.keys(updatedFieldValidation).length > 0) {
                setFieldValidations(dispatch, fieldValidation, updatedFieldValidation, true);
            }

            track.info([{ key: 'partner', value: whitelabelPartner }]);
        }
    }, [
        searchParams,
        product,
        selection,
        fieldValidation,
        hasInitPartner,
        dispatch,
        getCustomerDetails,
        getWhitelabelPartner
    ]);
};

/* ========================================= Main Component ========================================= */
const Root = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [{ selection, model, activePage, fieldValidation }, dispatch] = useStateValue();
    const config = useConfigContext();
    const vehicle = useVehicleContext();
    const user = useUserContext();
    const pContext = usePackageContext();
    const { flow, canContinue, setFlowBlocked } = useFlowContext();
    const { logFinalizePurchaseError, logFinalizePurchaseEmailError } = useLoggerContext();

    const productPages = pages[config.product];
    const [currentPage, setCurrentPage] = useState<IRouterPage>(productPages[0]);
    const [nextPage, setNextPage] = useState<any>();
    const [previousPage, setPreviousPage] = useState<any>();
    const [loadingNextPage, setLoadingNextPage] = useState<boolean>(false);
    const [didHandleFinalize, setDidHandleFinalize] = useState<boolean>(false);

    const pathname = useGetProcessedPathname(location.pathname);
    const [pageIndex, setPageIndex] = useState(0);
    const packageIndex = flow
        .filter((value) => value.key !== 'package-loader')
        .findIndex((value) => value.key === 'package');

    const isReceiptPage = pathname.includes('/kvittering');
    const showNordeaBanner =
        config.product === ProductNames.CAR &&
        user.loggedIn &&
        !selection.leasingCar &&
        selection.vehicleSelectionMode === 'MM' &&
        currentPage.key === 'package';

    const handleFinalize = useCallback(async () => {
        try {
            await finalizePurchase(
                { config, dispatch, selection, model, user, logFinalizePurchaseError, logFinalizePurchaseEmailError },
                pContext
            );
            track.info([{ key: 'ec.purchase', value: 'before tracking' }]);
            await ec.purchase({ config, selection, pContext });
            track.info([{ key: 'ec.purchase', value: 'after tracking' }]);
            setDidHandleFinalize(true);
        } catch (err) {
            const index = flow.map((p: IRouterPage) => p.key).indexOf('confirm');
            const redirectToPage = flow[index].path;
            const errorData: TError = {
                errorMessage: 'Kunne ikke afslutte dit køb',
                redirectToPage,
                hasError: true,
                dispatch,
                operation: 'Failing finishing the buy'
            };
            setError({ ...errorData });
        }
    }, [
        config,
        dispatch,
        model,
        selection,
        user,
        flow,
        pContext,
        logFinalizePurchaseError,
        logFinalizePurchaseEmailError
    ]);

    // check to see if some alt-conditions needs to be handled
    useEffect(() => {
        if (currentPage.altConditions) {
            if (currentPage.altConditions === 'checkForFullCustomer3738' && vehicle.noContent3738) {
                currentPage.title = currentPage.altTitle;
                currentPage.primaryBtnText = currentPage.altPrimaryBtnText;
            }
        }
    }, [currentPage, vehicle.noContent3738]);

    // Check for finalize
    useEffect(() => {
        if (
            currentPage &&
            currentPage.key === 'finalize' &&
            selection.didClickFinalize &&
            user.loggedIn &&
            canContinue(currentPage)
        ) {
            (async () => {
                pContext.selectedPackage && (await handleFinalize());
            })();
        }
    }, [currentPage, selection.didClickFinalize, user.loggedIn, pContext.selectedPackage, canContinue, handleFinalize]);

    // check if page requires login and not logged in
    useEffect(() => {
        if (currentPage?.requiresLogin && !user.loggedIn) {
            login(currentPage.path, config);
        }
    }, [config, currentPage?.path, currentPage?.requiresLogin, user.loggedIn]);

    useEffect(() => {
        if (activePage !== currentPage) {
            dispatch({
                type: SET_ACTIVE_PAGE,
                payload: currentPage
            });
        }
    }, [activePage, currentPage, dispatch]);

    // Check if the flow was initialized as a whitelabel/partner flow
    useInitiateWhiteLabelPartner(config.product, selection, fieldValidation, dispatch);

    // Set current page, next page
    useEffect(() => {
        if (!isReceiptPage) {
            const pathnameArr = pathname.split('/');
            let pageIndex = flow.map((p: IRouterPage) => p.path).indexOf(`/${pathnameArr[1]}`); // only check first element in path

            if (pageIndex === -1 && pathname.includes('sporing')) {
                pageIndex = 0;
            }

            const _currentPage = flow[pageIndex];
            setPageIndex(pageIndex);
            setCurrentPage(_currentPage);

            const nPage = flow[pageIndex + 1] || undefined;
            const currPage = pageIndex > -1 ? flow[pageIndex] : undefined;

            if (nPage.path === '/kvittering' && didHandleFinalize && canContinue(_currentPage)) {
                setDidHandleFinalize(false);
                const url = `${window.location.origin}${config.appBasePath}${nPage.path}`;
                window.location.assign(url);
            } else if (!!currPage && nPage !== nextPage) {
                track.navigate(document.URL, `${productName(config.product)} | ${pathname.split('/')[1]}`);
                setCurrentPage(currPage);
                setNextPage(nPage);

                // find previous page
                const previousPage = pageIndex > 0 ? flow[pageIndex - 1] : flow[0];
                // if previouspage is "pseudo" page / loader page, go back one extra step
                setPreviousPage(previousPage.isLoaderPage ? flow[pageIndex - 2] : previousPage);
            } else if (!currPage) {
                // if the user requests a page not in the current flow (ie. user logged in but clicks back to previous screen)
                navigate(productPages[0].path);
            }
        }
    }, [
        config.appBasePath,
        config.product,
        isReceiptPage,
        didHandleFinalize,
        flow,
        nextPage,
        pathname,
        productPages,
        navigate,
        canContinue
    ]);

    /**
     * Handle page click "Continue" button
     */
    const handleContinue = () => {
        try {
            setLoadingNextPage(true);
            if (canContinue(currentPage) && (!vehicle.noContent3738 || !currentPage.altConditions)) {
                // run continue callback fn
                if (currentPage.continueFn) {
                    currentPage.continueFn({ config, dispatch, selection, model, user, pContext });
                }

                // save user input and model in session storage
                saveToCache({ selection, model });

                navigate(nextPage.path);

                defaultTracking(
                    currentPage,
                    selection,
                    Object.keys(selection).filter((k) => !!selection[k])
                );
            } else if (vehicle.noContent3738 && currentPage.altConditions) {
                setFlowBlocked(true);
            }

            setLoadingNextPage(false);
        } catch (error) {
            setLoadingNextPage(false);
        }
    };

    const getProgressbarJsx = () => {
        const totalSteps = flow.filter((p: ProductPage) => p.progressBarStep) as ProductPage[];

        // some pages can be assigned the same progressBarStep value in the PageConfig, if they are to be treated as part of the progress bar step,
        //  e.g. the "Who are you" step, which consists of 3 pages (existing customer, new customer MitID, new customer without MitID) but is to
        // be treated as one collective step in the progess bar
        const uniqueProgressbarSteps = totalSteps.map((s) => s.progressBarStep).filter((v, i, a) => a.indexOf(v) === i);
        const currentStep = uniqueProgressbarSteps.indexOf(currentPage.progressBarStep) + 1;

        return (
            <ProgressBar
                currentStep={currentStep}
                totalSteps={uniqueProgressbarSteps.length}
                style={{ marginBottom: 30 }}
            />
        );
    };

    return (
        <>
            {!isReceiptPage && (
                <SiteHeader logoHref="https://topdanmark.dk/forsikringer">
                    <ActionMenuItem>
                        <button
                            className="site-header-menu-item-link"
                            data-testid="close-button"
                            onClick={() => (window.location.href = `${window.location.origin}/forsikringer`)}
                        >
                            <FlosIcon shape="close" size={16} />
                        </button>
                    </ActionMenuItem>
                </SiteHeader>
            )}

            <div className={`page-wrapper`}>
                {!isReceiptPage && pageIndex < packageIndex && (
                    <div className="page-heading">{productName(config.product)}</div>
                )}
                {!isReceiptPage && !currentPage?.hideProgressbar && getProgressbarJsx()}
                <Suspense fallback={<LoadingDots centered={true} />}>
                    <div className={`page-content-wrapper ${loadingNextPage ? 'page-content-wrapper--loading' : ''}`}>
                        <Outlet />
                    </div>
                    {!isReceiptPage && (
                        <PageNavigationWrapper
                            size={currentPage.size}
                            wrapperChildren={
                                <>
                                    <PageNavigation
                                        currentPage={currentPage}
                                        loading={loadingNextPage}
                                        previousPage={previousPage}
                                        canContinue={canContinue(currentPage)}
                                        handleContinue={() => handleContinue()}
                                    />
                                    {currentPage.hasGiftcard && selection.partner !== undefined && <Giftcard />}
                                    {showNordeaBanner && <NordeaBanner />}
                                </>
                            }
                        />
                    )}
                </Suspense>

                {!currentPage.isLoaderPage && selection.partner !== undefined && (
                    <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '10px' }}>
                        <WhiteLabel partner={selection.partner} />
                    </div>
                )}
            </div>
            {loadingNextPage && <LoadingDots centered={true} />}
        </>
    );
};

export default Root;
